<template>
  <NotFound v-if="residencyNotFound" />
  <div v-else-if="residencyBySlug" class="px-4 py-12 md:py-16 mx-auto max-w-6xl">
    <div
      id="residency-header"
      class="flex flex-col items-start justify-start gap-4 md:flex-row md:items-end md:justify-between"
    >
      <div id="residency-details">
        <p v-if="residencyBySlug.tagline" id="residency-tagline" class="text-fg-disclaimer">
          {{ residencyBySlug.tagline }}
        </p>
        <p v-else class="text-fg-disclaimer">Residency at {{ venue_name }}</p>
        <h1 class="text-4xl font-semibold font-heading">
          {{ residencyBySlug.name }}
        </h1>
        <p class="text-button-guest-primary text-lg">
          <span>{{ settingStore.fullDate(residencyBySlug.start_date) }}</span>
          <span v-if="residencyBySlug.end_date">
            - {{ settingStore.fullDate(residencyBySlug.end_date) }}
          </span>
        </p>
      </div>

      <!--BUY TICKETS BUTTON-->
      <a
        class="performance-button py-3 px-6 rounded font-semibold text-base font-button bg-button-guest-primary text-button-label min-w-[150px] max-w-[200px] truncate uppercase text-center"
        id="buy-tickets-button"
        href="#buy-tickets-list"
      >
        Buy Tickets
      </a>
    </div>

    <!--SHOW IMAGE-->
    <div v-if="show" class="mt-6 w-full">
      <picture id="residency-image">
        <source :srcset="show.srcset.rectSm.src" type="image/webp" media="(max-width: 768px)" />
        <source :srcset="show.srcset.rectXl.src" type="image/webp" />
        <img :src="show.srcset.rectXl.src" :alt="show.name" class="w-full h-auto" />
      </picture>
    </div>

    <!--INTRO-->
    <p v-if="residencyBySlug.intro" id="residency-intro" class="mt-6 whitespace-pre-wrap">
      {{ residencyBySlug.intro }}
    </p>

    <!--PERFORMANCE LIST-->
    <div id="buy-tickets-list" class="pt-10">
      <h2 class="text-xl font-semibold font-heading">Tickets</h2>
      <template v-if="residencyShows.length > 0">
        <div
          v-for="show in residencyShows"
          :key="show.id"
          :id="`show-${show.id}-date-${show.date}`"
          class="py-4 border-b border-linear-g-primary flex flex-col gap-1 items-start justify-start"
        >
          <div class="show-header">
            <p class="text-sm line-clamp-1">{{ show.name }}</p>
            <p>{{ settingStore.mediumDate(show.datetime) }}</p>
          </div>
          <div class="flex gap-2 justify-end items-start flex-wrap w-full">
            <PerformanceButton
              v-for="performance in show.performances"
              :key="performance.id"
              :performance="performance"
              :error="error"
              :id="`performance-${performance.id}`"
              :forcePurchase="
                Boolean(inviteCode) || checkoutStore.isPerformanceUnlockedByInviteCode(performance)
              "
            />
          </div>
        </div>
      </template>
      <p v-else>No upcoming shows</p>
    </div>

    <!--SOCIAL PROOF-->
    <blockquote
      v-if="residencyBySlug.social_proof"
      id="residency-social-proof"
      class="mt-8 border-l-4 border-button-guest-primary pl-4 italic text-lg whitespace-pre-wrap"
    >
      {{ residencyBySlug.social_proof }}
    </blockquote>

    <div class="mt-12 pt-10 flex flex-col-reverse gap-8 lg:flex-row">
      <div v-if="show" class="flex flex-col gap-2 lg:grow">
        <!--YOUTUBE VIDEO URL-->
        <div v-if="youtubeEmbedSrc" id="residency-video" class="aspect-video w-full">
          <iframe
            :src="youtubeEmbedSrc"
            title="Residency video"
            class="w-full h-full"
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
            :allow="YOUTUBE_IFRAME_ALLOW"
            allowfullscreen
          ></iframe>
        </div>
      </div>
      <div class="lg:w-60 flex flex-col gap-2">
        <!--SHARE-->
        <ShareButton
          id="residency-share-button"
          :url="residencyPublicLink"
          :title="residencyBySlug.name"
          :text="`${residencyBySlug.name} at ${venue_name}`"
        />

        <!--EXTERNAL LINKS-->
        <div v-if="sortedExternalLinks.length > 0" id="residency-external-links">
          <ul class="flex flex-col gap-2">
            <li
              v-for="link in sortedExternalLinks"
              :key="link.id ?? `${link.position}-${link.url}`"
            >
              <a
                :id="`residency-external-link-${link.id ?? link.position}`"
                :href="safeHref(link.url)"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 text-secondary hover:opacity-80"
              >
                <component :is="iconComponentByName[iconFor(link)]" class="w-5 h-5" />
                <span>{{ link.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!--SHOW DESCRIPTION-->
    <div
      v-if="show"
      id="residency-description"
      class="mt-8 whitespace-pre-wrap"
      v-html="sanitizedResidencyDescription"
    />

    <!--ABOUT VENUE-->
    <section v-if="about_venue" id="residency-about-venue" class="mt-16">
      <h2 class="text-2xl font-semibold font-heading">About {{ venue_name }}</h2>
      <p class="mt-6 whitespace-pre-wrap">{{ about_venue }}</p>
    </section>

    <div class="mt-16 flex items-start justify-start">
      <!--BUY TICKETS BUTTON-->
      <a
        class="performance-button py-3 px-6 rounded font-semibold text-base font-button bg-button-guest-primary text-button-label min-w-[150px] max-w-[200px] truncate uppercase text-center"
        id="buy-tickets-button-bottom"
        href="#buy-tickets-list"
      >
        Buy Tickets
      </a>
    </div>
  </div>
  <div v-else class="container px-4 py-12 md:py-16 mx-auto">
    <p>Loading Residency...</p>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, onMounted, onServerPrefetch, ref, toRefs } from 'vue'
import { useRoute } from 'vue-router'

import { usePostHog } from '@/common/composables/usePostHog'
import ShareButton from '@/modules/guestUi/components/molecules/ShareButton.vue'
import PerformanceButton from '@/modules/guestUi/components/organisms/PerformanceButton.vue'
import { DisplayShow } from '@/modules/guestUi/interfaces/shows'
import NotFound from '@/modules/guestUi/pages/NotFound.vue'
import {
  DEFAULT_RESIDENCY_LINK_ICON,
  ResidencyLinkIconName,
  iconComponentByName,
  isResidencyLinkIconName,
} from '@/modules/residency/constants/ResidencyExternalLinkIcons'
import { buildResidencyJsonLd } from '@/modules/residency/utils/seo'
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl } from '@/modules/residency/utils/youtube'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError } from '@/utils/errors'
import { sanitizeHtmlForClient } from '@/utils/sanitize'
import { sortBy, todayIsoStr } from '@/utils/utils'

import { ResidencyExternalLinkRead, ResidencyRead, ShowRead } from '@generated/types'

const error = ref<ErrorData>(blankError())
const checkoutStore = useCheckoutStore()
const settingStore = useSettingStore()
const { venue_name, about_venue } = storeToRefs(settingStore)

const props = defineProps<{ slug: string }>()
const { slug } = toRefs(props)
const residencyBySlug = ref<ResidencyRead | null>(null)
const residencyNotFound = ref(false)

const route = useRoute()
const inviteCode = computed<string | undefined>(() => route.query.invite as string | undefined)
onMounted(async () => {
  // Tag residency → checkout funnel events with which page version the visitor saw.
  usePostHog().registerForSession({ residency_page_version: 'landing_v2' })
  await checkoutStore.abandonAndClear()
  checkoutStore.setInviteCode(inviteCode.value)
})

async function load() {
  try {
    try {
      residencyNotFound.value = false
      residencyBySlug.value = await checkoutStore.getResidency(slug.value)
      if (!residencyBySlug.value || isEmpty(residencyBySlug.value)) {
        residencyNotFound.value = true
      }
    } catch (e) {
      residencyNotFound.value = true
    }
    if (residencyNotFound.value) {
      settingStore.ssrTitle = settingStore.venue_name
      settingStore.ssrVideoUrl = null
      settingStore.ssrJsonLd = null
      settingStore.notFound = true
      return
    }
    settingStore.ssrTitle = residencyBySlug.value?.name || settingStore.venue_name
    const rawDescription =
      residencyBySlug.value?.description || residencyBySlug.value?.show?.description || ''
    settingStore.ssrDescription = sanitizeHtmlForClient(rawDescription).replace(/<[^>]+>/g, '')
    const videoUrl = residencyBySlug.value?.video_url || ''
    const galleryImage = residencyBySlug.value?.show?.srcset?.rectLg?.src || ''
    settingStore.ssrImage = galleryImage || getYouTubeThumbnailUrl(videoUrl) || ''
    settingStore.ssrVideoUrl = videoUrl ? getYouTubeEmbedUrl(videoUrl) : null
    settingStore.ssrJsonLd = buildResidencyJsonLd(residencyBySlug.value)
  } finally {
    settingStore.ssrPrefetchDone = true
  }
}

onServerPrefetch(load)
onBeforeMount(load)

const residencyPublicLink = computed<string>(function () {
  if (!residencyBySlug.value) return ''
  // Match the route this page is actually mounted on (/r/ when flagged on, /rd/ direct),
  // so the share/canonical URL doesn't leak traffic to the always-v2 /rd/ route. Build from
  // the canonical slug (not route.path) so aliases/casing/trailing-slash variants don't echo back.
  const base = route.path.startsWith('/rd/') ? '/rd/' : '/r/'
  return `${settingStore.siteDomain}${base}${residencyBySlug.value.slug}`
})
const show = computed<ShowRead | undefined>(() => residencyBySlug.value?.show)
const sanitizedResidencyDescription = computed(() =>
  sanitizeHtmlForClient(residencyBySlug.value?.description || show.value?.description),
)

const youtubeEmbedSrc = computed<string | null>(() =>
  getYouTubeEmbedUrl(residencyBySlug.value?.video_url ?? ''),
)

const sortedExternalLinks = computed<ResidencyExternalLinkRead[]>(() =>
  [...(residencyBySlug.value?.external_links ?? [])].sort((a, b) => a.position - b.position),
)

function iconFor(link: ResidencyExternalLinkRead): ResidencyLinkIconName {
  return isResidencyLinkIconName(link.icon) ? link.icon : DEFAULT_RESIDENCY_LINK_ICON
}

function safeHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : '#'
}

const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share'

const residencyShows = computed<DisplayShow[]>(function () {
  if (!residencyBySlug.value) return []

  const shows: DisplayShow[] = []
  const current = todayIsoStr()
  for (const performance of residencyBySlug.value!.performances) {
    const performanceDate = settingStore.dateIsoStr(performance.datetime)
    if (performanceDate < current) continue
    const show = shows.find(show => show.date === performanceDate && show.id == performance.show_id)

    if (show) {
      show.performances.push(performance)
      sortBy(show.performances, 'datetime')
    } else {
      const desc = performance.show?.description || ''
      shows.push({
        ...performance.show,
        id: performance.show_id,
        date: performanceDate,
        datetime: performance.datetime,
        dateStr: settingStore.mediumDate(performance.datetime),
        description: desc,
        position: performance.show?.position || [],
        performances: [performance],
      })
    }
  }

  return sortBy(shows, 'datetime')
})
</script>
